import { Link } from "react-router-dom";
import { ArrowUpRight, BriefcaseBusiness, CalendarDays, Compass, GraduationCap, Handshake, LineChart, Newspaper, Users } from "lucide-react";
import { fmt } from "@/components/hero/LiveIndex";
import { useHeroLiveStats } from "@/lib/useHeroLiveStats";
import { ACADEMY_LAUNCHED } from "@/lib/academy/launch";

const cardsFor = (jobs) => [
  { id: "jobs", href: "/#jobs", Icon: BriefcaseBusiness, title: "Job Board", body: `${fmt(jobs?.feed_total)} open roles from ${fmt(jobs?.employer_count)} live employer feeds, US first.`, tag: "Live", size: "lg:col-span-2 lg:row-span-2", dark: true, roles: jobs?.jobs?.slice(0, 3) || [] },
  { id: "careers", href: "/careers", Icon: Compass, title: "Careers Hub", body: "Find your path, role guides, certifications and job alerts.", size: "lg:col-span-2" },
  { id: "academy", href: "/academy", Icon: GraduationCap, title: "DCYB Academy", body: "Learn the industry behind AI.", tag: ACADEMY_LAUNCHED ? null : "Soon" },
  { id: "wire", href: "/wire", Icon: Newspaper, title: "The Wire", body: "News and signals from across digital infrastructure." },
  { id: "markets", href: "/#market", Icon: LineChart, title: "Markets", body: "Live prices for the companies powering data centers.", tag: "Live" },
  { id: "events", href: "/#events", Icon: CalendarDays, title: "Events", body: "Meetups, panels and industry gatherings." },
  { id: "partners", href: "/#certified-partner", Icon: Handshake, title: "Partners", body: "Supporter, Partner and Anchor programs for companies." },
  { id: "get-involved", href: "/get-involved", Icon: Users, title: "Get Involved", body: "Ways to join, support and work with the DCYB community." },
];

const Tag = ({ label, dark }) => <span className={`font-mono-label text-[9px] !tracking-normal px-2 py-1 ${label === "Soon" ? "bg-[#D00508] text-white" : dark ? "bg-[#10B981]/15 text-[#34D399]" : "bg-[#EFFBF5] text-[#078552]"}`}>{label}</span>;

const Roles = ({ roles }) => (
  <span data-testid="landing-explore-newest-roles" className="block border-t border-white/15">
    <span className="block pt-4 pb-2 font-mono-label text-[10px] !tracking-normal text-[#9CA1AA]">Newest roles</span>
    {roles.map((job) => (
      <span key={job.id} className="flex items-center justify-between gap-3 py-2.5 border-b border-white/10 min-w-0">
        <span className="min-w-0"><span className="block text-sm font-semibold text-white truncate">{job.title}</span><span className="block text-xs text-[#9CA1AA] truncate">{job.company} · {job.location}</span></span>
        <span aria-hidden="true" className="shrink-0 live-dot" />
      </span>
    ))}
  </span>
);

const Card = ({ card: { id, href, Icon, title, body, tag, size = "", dark, roles } }) => {
  const hashLink = href.startsWith("/#");
  const className = `group relative flex h-full min-h-[190px] flex-col justify-between gap-6 p-6 sm:p-7 border transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0F1117] ${dark ? "bg-[#0F1117] border-[#0F1117] text-white hover:shadow-[0_24px_44px_rgba(15,17,23,0.3)]" : "chrome-surface border-[#D5D8DD] hover:border-[#0F1117] hover:shadow-[0_20px_40px_rgba(15,17,23,0.1)]"}`;
  const inner = (
    <>
      <span className="flex items-start justify-between gap-3">
        <span className={`w-12 h-12 flex items-center justify-center border ${dark ? "border-white/20 bg-white/5" : "border-[#D5D8DD] bg-white"}`}><Icon size={20} aria-hidden="true" /></span>
        <span className="flex items-center gap-2">{tag && <Tag label={tag} dark={dark} />}<ArrowUpRight size={18} aria-hidden="true" className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></span>
      </span>
      {roles?.length > 0 && <Roles roles={roles} />}
      <span>
        <span className={`block font-display ${dark ? "text-3xl sm:text-4xl" : "text-xl"} text-balance`}>{title}</span>
        <span className={`block mt-2 text-sm leading-relaxed ${dark ? "text-[#C3C7CF] text-base" : "text-[#3D4049]"}`}>{body}</span>
      </span>
    </>
  );
  return (
    <li className={size}>
      {hashLink ? <a href={href} data-testid={`landing-explore-card-${id}`} className={className}>{inner}</a> : <Link to={href} data-testid={`landing-explore-card-${id}`} className={className}>{inner}</Link>}
    </li>
  );
};

export const ExploreGrid = () => {
  const live = useHeroLiveStats({ jobsCount: 3 });
  return (
    <section id="explore-dcyb" data-testid="landing-explore-grid" aria-labelledby="landing-explore-title" className="bg-white border-b hairline scroll-mt-20" data-chat-safe-area="phone">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <p className="font-mono-label text-[11px] !tracking-normal text-[#5F6168]">/ Explore DCYB</p>
        <h2 id="landing-explore-title" className="mt-4 mb-10 font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] sm:leading-[1.1] text-[#0F1117] text-balance">Pick Your Way In</h2>
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 lg:auto-rows-fr gap-4">
          {cardsFor(live.jobs).map((card) => <Card key={card.id} card={card} />)}
        </ul>
      </div>
    </section>
  );
};
